import React, { useState, useEffect } from 'react';
import { Statuses } from 'interfaces/ITask.interface';
import { CQAppConstant } from 'app-constants';
import { useTranslation } from 'react-i18next';
import { IBaseTask } from 'interfaces/IUpgradeSchema.interface';
import { CQSubTask } from '../cq-sub-task';
import Accordion from '@salesforce/design-system-react/components/accordion';
import AccordionPanel from '@salesforce/design-system-react/components/accordion/panel';

export interface CQMigrationTaskV2Props {
    task: IBaseTask;
}

interface ICQMigrationTaskRowProps {
    subtask: { 
        name: string; 
        status: number;
        objectName?: string;
        objectApiName?: string;
        scriptName?: string;
        toProcess?: number;
        completed?: number;
        failed?: number;
        showObjectName?: boolean;
        showObjectApiName?: boolean;
        uniqueKey?: string;
    };
}

/**
 * Renders a single migration task row with status and counts.
 * @param prop - Row props containing subtask details to display
 */
function CQMigrationTaskRow(prop: ICQMigrationTaskRowProps) {
    const { t } = useTranslation();
    const [status, setStatus] = useState<string>(CQAppConstant.TASK_STATUS_LABELS.PENDING);

    useEffect(() => {
        let statusValue = prop.subtask.status;
        switch (statusValue) {
            case Statuses.Completed: setStatus(CQAppConstant.TASK_STATUS_LABELS.COMPLETED); break;
            case Statuses.Pending: setStatus(CQAppConstant.TASK_STATUS_LABELS.PENDING); break;
            case Statuses.Failed: setStatus(CQAppConstant.TASK_STATUS_LABELS.FAILED); break;
            default: setStatus(CQAppConstant.TASK_STATUS_LABELS.NOT_STARTED); break;
        }
    }, [prop.subtask, prop.subtask.status])

    return (
        <tr className="slds-hint-parent">
            <td data-label={t("task_migration_v2_object_name")}>
                {prop.subtask.showObjectName ? (prop.subtask.objectName || '') : ''}
            </td>
            <td data-label={t("task_migration_v2_object_api_name")}>
                {prop.subtask.showObjectApiName ? (prop.subtask.objectApiName || '') : ''}
            </td>
            <td data-label={t("task_migration_v2_migration_script")}>{prop.subtask.scriptName || ''}</td>
            <td data-label={t("task_subtask_status_column")}>
                <span className={`slds-badge ${status === CQAppConstant.TASK_STATUS_LABELS.COMPLETED ? 'slds-theme_success' : status === CQAppConstant.TASK_STATUS_LABELS.FAILED ? 'slds-theme_error' : status === CQAppConstant.TASK_STATUS_LABELS.PENDING ? 'slds-theme_warning' : ''}`}>
                    {status}
                </span>
            </td>
            <td data-label={t("task_migration_v2_to_process")}>{prop.subtask.toProcess || 0}</td>
            <td data-label={t("task_migration_v2_completed")}>{prop.subtask.completed || 0}</td>
            <td data-label={t("task_migration_v2_failed")}>{prop.subtask.failed || 0}</td>
        </tr>
    );
}

/**
 * Renders migration v2 task grouped by phase with accordion sections.
 * @param props - Component props containing the task inputs
 */
export default function CQMigrationTaskV2(props: CQMigrationTaskV2Props) {
    const { t } = useTranslation();
    const task = props.task;
    
    const [expandedPanels, setExpandedPanels] = useState<{[key: string]: boolean}>({
        [CQAppConstant.MIGRATION_V2_PANEL_IDS.PRE_MIGRATION]: true,
        [CQAppConstant.MIGRATION_V2_PANEL_IDS.MIGRATION_SCRIPTS]: true,
        [CQAppConstant.MIGRATION_V2_PANEL_IDS.POST_MIGRATION]: true
    });

    const handleTogglePanel = (panelId: string) => {
        setExpandedPanels(prev => ({
            ...prev,
            [panelId]: !prev[panelId]
        }));
    };

    let preMigrationTasks = task.inputs.preMigrationTasks && Object.entries(task.inputs.preMigrationTasks).length > 0 ?
        Object.entries(task.inputs.preMigrationTasks).map((subSteps) => {
            return { name: subSteps[0], status: subSteps[1] as number };
        }) : [];

    let migrationScripts = task.inputs.subTasks && Object.entries(task.inputs.subTasks).length > 0 ?
        Object.entries(task.inputs.subTasks)
            .map((subSteps) => {
                const value = subSteps[1];
                if (typeof value === CQAppConstant.MIGRATION_V2_PROPERTY_NAMES.OBJECT && value !== null && CQAppConstant.MIGRATION_V2_PROPERTY_NAMES.STATUS in value) {
                    return { name: subSteps[0], ...value };
                }
                return { name: subSteps[0], status: value };
            })
            // Sort by object name to ensure consecutive grouping
            .sort((a, b) => {
                const objA = a.objectName || '';
                const objB = b.objectName || '';
                if (objA === objB) {
                    // If same object, sort by script name
                    return (a.scriptName || a.name || '').localeCompare(b.scriptName || b.name || '');
                }
                return objA.localeCompare(objB);
            })
            // Add display flags and unique keys
            .map((scriptData, index, array) => {
                const previousObjectName = index > 0 ? array[index - 1].objectName : null;
                const currentObjectName = scriptData.objectName;
                
                // Show object name/API name only if it's different from previous row
                const showObjectInfo = index === 0 || previousObjectName !== currentObjectName;
                
                return {
                    ...scriptData,
                    showObjectName: showObjectInfo,
                    showObjectApiName: showObjectInfo,
                    uniqueKey: `${currentObjectName || CQAppConstant.MIGRATION_V2_PROPERTY_NAMES.UNKNOWN}-${scriptData.scriptName || scriptData.name}-${index}`
                };
            }) : [];

    let postMigrationTasks = task.inputs.postMigrationTasks && Object.entries(task.inputs.postMigrationTasks).length > 0 ?
        Object.entries(task.inputs.postMigrationTasks).map((subSteps) => {
            return { name: subSteps[0], status: subSteps[1] as number };
        }) : [];

    return (
        <div className="slds-m-top_medium">
            {preMigrationTasks.length > 0 && (
                <div className="slds-theme_shade slds-m-bottom_small slds-p-around_small">
                    <Accordion>
                        <AccordionPanel
                            expanded={expandedPanels[CQAppConstant.MIGRATION_V2_PANEL_IDS.PRE_MIGRATION]}
                            id={CQAppConstant.MIGRATION_V2_PANEL_IDS.PRE_MIGRATION}
                            onTogglePanel={() => handleTogglePanel(CQAppConstant.MIGRATION_V2_PANEL_IDS.PRE_MIGRATION)}
                            summary={<div className="slds-theme_shade"><span className="slds-text-heading_small">{t("task_migration_v2_pre_migration_tasks")}</span></div>}
                        >
                            <div className="slds-scrollable_x slds-theme_shade slds-p-around_small">
                                <table className="slds-table slds-table_cell-buffer slds-table_bordered">
                                    <thead>
                                        <tr className="slds-line-height_reset">
                                            <th scope="col">
                                                <div className="slds-truncate" title={t("task_subtask_name_column")}>{t("task_subtask_name_column")}</div>
                                            </th>
                                            <th scope="col">
                                                <div className="slds-truncate" title={t("task_subtask_status_column")}>{t("task_subtask_status_column")}</div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preMigrationTasks.map((subTask: any, index: number) => (
                                            <CQSubTask key={index} subtask={subTask} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </AccordionPanel>
                    </Accordion>
                </div>
            )}

            {migrationScripts.length > 0 && (
                <div className="slds-theme_shade slds-m-bottom_small slds-p-around_small">
                    <Accordion>
                        <AccordionPanel
                            expanded={expandedPanels[CQAppConstant.MIGRATION_V2_PANEL_IDS.MIGRATION_SCRIPTS]}
                            id={CQAppConstant.MIGRATION_V2_PANEL_IDS.MIGRATION_SCRIPTS}
                            onTogglePanel={() => handleTogglePanel(CQAppConstant.MIGRATION_V2_PANEL_IDS.MIGRATION_SCRIPTS)}
                            summary={<div className="slds-theme_shade"><span className="slds-text-heading_small">{t("task_migration_v2_migration_scripts")}</span></div>}
                        >
                            <div className="slds-scrollable_x slds-theme_shade slds-p-around_small">
                                <table className="slds-table slds-table_cell-buffer slds-table_bordered">
                                <thead>
                                    <tr className="slds-line-height_reset">
                                        {CQAppConstant.MIGRATION_V2_COLUMNS.map(column => (
                                            <th key={column.key} scope="col">
                                                <div className="slds-truncate" title={column.label}>{column.label}</div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {migrationScripts.map((subTask: any) => (
                                        <CQMigrationTaskRow key={subTask.uniqueKey} subtask={subTask} />
                                    ))}
                                </tbody>
                                </table>
                            </div>
                        </AccordionPanel>
                    </Accordion>
                </div>
            )}

            {postMigrationTasks.length > 0 && (
                <div className="slds-theme_shade slds-m-bottom_small slds-p-around_small">
                    <Accordion>
                        <AccordionPanel
                            expanded={expandedPanels[CQAppConstant.MIGRATION_V2_PANEL_IDS.POST_MIGRATION]}
                            id={CQAppConstant.MIGRATION_V2_PANEL_IDS.POST_MIGRATION}
                            onTogglePanel={() => handleTogglePanel(CQAppConstant.MIGRATION_V2_PANEL_IDS.POST_MIGRATION)}
                            summary={<div className="slds-theme_shade"><span className="slds-text-heading_small">{t("task_migration_v2_post_migration_tasks")}</span></div>}
                        >
                            <div className="slds-scrollable_x slds-theme_shade slds-p-around_small">
                                <table className="slds-table slds-table_cell-buffer slds-table_bordered">
                                    <thead>
                                        <tr className="slds-line-height_reset">
                                            <th scope="col">
                                                <div className="slds-truncate" title={t("task_subtask_name_column")}>{t("task_subtask_name_column")}</div>
                                            </th>
                                            <th scope="col">
                                                <div className="slds-truncate" title={t("task_subtask_status_column")}>{t("task_subtask_status_column")}</div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {postMigrationTasks.map((subTask: any, index: number) => (
                                            <CQSubTask key={index} subtask={subTask} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </AccordionPanel>
                    </Accordion>
                </div>
            )}
        </div>
    );
}
